import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, Clock, Armchair, Store, X, Phone, Star, MessageSquare, Sparkles, Award } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function MyReservations() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewing, setReviewing] = useState(null); // reservation_id
    const [reviewStars, setReviewStars] = useState(0);
    const [reviewText, setReviewText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [points, setPoints] = useState({ points: 0, reviews_count: 0, points_per_review: 10, pending_reviews: [] });

    const load = async () => {
        try {
            const [r, p] = await Promise.all([
                api.get("/reservations/my"),
                api.get("/me/points").catch(() => ({ data: { points: 0, reviews_count: 0, points_per_review: 10, pending_reviews: [] } })),
            ]);
            setList(r.data);
            setPoints(p.data);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("MyReservations load failed:", err?.message || err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // Auto-refresh ogni 20s così il cliente vede subito i cambi pushati da
        // SmartTable / dal pannello ristoratore (arrivato, confermato, tavolo
        // assegnato) senza dover ricaricare manualmente la pagina.
        const id = setInterval(() => {
            if (document.visibilityState === "visible") load();
        }, 20_000);
        // Refresh anche quando l'utente torna sulla tab
        const onVis = () => { if (document.visibilityState === "visible") load(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    const hoursUntil = (r) => {
        try {
            const dt = new Date(`${r.date}T${r.slot_time}:00`);
            return (dt.getTime() - Date.now()) / 3_600_000;
        } catch {
            return null;
        }
    };

    const canCancel = (r) => {
        if (r.status === "cancelled") return false;
        if (r.arrival_status === "arrived" || r.arrival_status === "no_show") return false;
        const h = hoursUntil(r);
        return h !== null && h >= 1;
    };

    const mustCallRestaurant = (r) => {
        if (r.status === "cancelled") return false;
        if (r.arrival_status === "arrived" || r.arrival_status === "no_show") return false;
        const h = hoursUntil(r);
        return h !== null && h < 1 && h > -2; // within the active window
    };

    const canReview = (r) => r.arrival_status === "arrived" && !r.review_id && r.status !== "cancelled";

    const onCancel = async (r) => {
        if (!window.confirm("Confermare l'annullamento della prenotazione?")) return;
        try {
            await api.post(`/reservations/${r.reservation_id}/cancel`);
            toast.success("Prenotazione annullata");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    const openReview = (r) => {
        setReviewing(r.reservation_id);
        setReviewStars(0);
        setReviewText("");
    };

    const submitReview = async () => {
        if (reviewStars < 1 || reviewStars > 5) {
            toast.error("Seleziona da 1 a 5 stelle");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/reservations/${reviewing}/review`, { stars: reviewStars, text: reviewText });
            toast.success("Recensione pubblicata, grazie!");
            setReviewing(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-ink pt-32 pb-20 px-6" data-testid="my-reservations-page">
            <div className="max-w-5xl mx-auto">
                <div className="mb-12">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px w-10 bg-gold" />
                        <span className="text-gold text-xs tracking-[0.4em] uppercase">Il tuo archivio</span>
                    </div>
                    <h1 className="font-serif text-5xl lg:text-6xl text-white leading-tight">
                        Le mie <span className="italic text-gold">prenotazioni</span>
                    </h1>
                </div>

                {/* Points + pending review reminder */}
                <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="loyalty-section">
                    <div className="bg-gradient-to-br from-gold/15 to-gold/5 border border-gold/40 rounded-sm p-6 flex items-center gap-5" data-testid="points-card">
                        <div className="w-14 h-14 rounded-sm bg-gold/20 flex items-center justify-center flex-shrink-0">
                            <Award size={28} className="text-gold" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">I tuoi punti fedeltà</div>
                            <div className="font-serif text-4xl text-white mt-1" data-testid="points-total">{points.points}</div>
                            <div className="text-xs text-gray-400 mt-1">
                                {points.reviews_count} {points.reviews_count === 1 ? "recensione pubblicata" : "recensioni pubblicate"} · +{points.points_per_review} punti a recensione
                            </div>
                        </div>
                    </div>

                    {points.pending_reviews && points.pending_reviews.length > 0 ? (
                        <div className="bg-gold/10 border border-gold/40 rounded-sm p-6" data-testid="pending-reviews-card">
                            <div className="flex items-center gap-2 text-gold mb-2">
                                <Sparkles size={16} strokeWidth={1.5} />
                                <div className="text-[10px] uppercase tracking-[0.3em]">Ricordati di recensire</div>
                            </div>
                            <p className="text-sm text-white leading-snug">
                                Hai <strong className="text-gold">{points.pending_reviews.length}</strong> {points.pending_reviews.length === 1 ? "visita" : "visite"} senza recensione. Racconta la tua esperienza: <strong>più recensioni lasci, più punti guadagni</strong>, che sbloccheranno sconti maggiori!
                            </p>
                            <div className="mt-3 space-y-1 max-h-24 overflow-auto">
                                {points.pending_reviews.slice(0, 3).map((p) => {
                                    const res = list.find((x) => x.reservation_id === p.reservation_id);
                                    return (
                                        <button key={p.reservation_id}
                                            onClick={() => res && openReview(res)}
                                            className="w-full text-left text-xs text-gray-200 hover:text-gold border-b border-white/5 py-1 flex items-center justify-between gap-2"
                                            data-testid={`pending-review-${p.reservation_id}`}>
                                            <span className="truncate">{p.restaurant_name} · {p.date}</span>
                                            <Star size={11} className="flex-shrink-0" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-ink-surface border border-white/10 rounded-sm p-6 flex items-center gap-4" data-testid="pending-reviews-empty">
                            <Sparkles size={20} className="text-gold flex-shrink-0" strokeWidth={1.5} />
                            <p className="text-sm text-gray-400">
                                Nessuna recensione in sospeso. <span className="text-gray-500">Dopo ogni visita confermata dal ristorante potrai lasciare la tua valutazione e guadagnare punti.</span>
                            </p>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-gray-500">Caricamento...</div>
                ) : list.length === 0 ? (
                    <div className="bg-ink-surface border border-white/10 rounded-sm p-12 text-center">
                        <p className="text-gray-400 mb-6 font-serif text-xl">Nessuna prenotazione ancora.</p>
                        <Link to="/" className="inline-block bg-gold text-black px-6 py-3 rounded-sm hover:bg-gold-light font-semibold" data-testid="go-directory-btn">
                            Scopri i ristoranti
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {list.map((r) => (
                            <div key={r.reservation_id} className="bg-ink-surface border border-white/10 rounded-sm p-6 hover:border-gold/40 transition-colors" data-testid={`reservation-${r.reservation_id}`}>
                                <div className="flex items-start justify-between flex-wrap gap-4">
                                    <div className="space-y-3 flex-1 min-w-0">
                                        <div className="flex items-center gap-2 text-gold">
                                            <Store size={14} strokeWidth={1.5} />
                                            <Link to={`/r/${r.restaurant_slug}`} className="font-serif text-xl hover:text-gold-light">
                                                {r.restaurant_name}
                                            </Link>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-4 text-sm">
                                            <div className="flex items-center gap-2 text-white">
                                                <Calendar size={14} className="text-gold" strokeWidth={1.5} />
                                                <span className="font-semibold">{r.date}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-300">
                                                <Clock size={14} className="text-gold" strokeWidth={1.5} /> {r.slot_time}
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-300">
                                                <Users size={14} className="text-gold" strokeWidth={1.5} /> {r.party_size} coperti
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-300">
                                                <Armchair size={14} className="text-gold" strokeWidth={1.5} /> Tavolo {r.table_number}
                                            </div>
                                        </div>
                                        {r.special_requests && <div className="text-xs text-gray-400 italic">"{r.special_requests}"</div>}
                                        {r.allergies && (
                                            <div className="text-xs text-rose-300/90 inline-flex items-center gap-1.5">
                                                <span className="uppercase tracking-widest text-[9px] text-rose-400/70">Allergie</span> {r.allergies}
                                            </div>
                                        )}
                                        {r.bringing_pet && (
                                            <div className="text-xs text-amber-300/90 inline-flex items-center gap-1.5">🐾 Animale domestico al seguito</div>
                                        )}
                                        {r.review_id && (
                                            <div className="text-xs text-emerald-400 inline-flex items-center gap-1"><Star size={12} /> Recensione inviata</div>
                                        )}
                                    </div>

                                    <div className="flex flex-col items-end gap-2">
                                        <div className="flex gap-2 items-center">
                                            <StatusBadge status={r.status} arrivalStatus={r.arrival_status} />
                                        </div>
                                        <div className="flex gap-2 flex-wrap justify-end">
                                            {canCancel(r) && (
                                                <button onClick={() => onCancel(r)}
                                                    className="inline-flex items-center gap-1.5 text-xs text-red-400 border border-red-500/40 px-3 py-1.5 rounded-sm hover:bg-red-500/10 uppercase tracking-widest"
                                                    data-testid={`cancel-btn-${r.reservation_id}`}>
                                                    <X size={12} /> Annulla
                                                </button>
                                            )}
                                            {mustCallRestaurant(r) && (
                                                <div className="inline-flex items-center gap-1.5 text-xs text-amber-400 border border-amber-500/40 px-3 py-1.5 rounded-sm uppercase tracking-widest"
                                                    data-testid={`call-restaurant-${r.reservation_id}`}>
                                                    <Phone size={12} /> Chiama il ristorante
                                                </div>
                                            )}
                                            {canReview(r) && (
                                                <button onClick={() => openReview(r)}
                                                    className="inline-flex items-center gap-1.5 text-xs text-gold border border-gold/50 px-3 py-1.5 rounded-sm hover:bg-gold/10 uppercase tracking-widest"
                                                    data-testid={`review-btn-${r.reservation_id}`}>
                                                    <Star size={12} /> Recensisci
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {reviewing && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" data-testid="review-modal">
                    <div className="bg-ink-surface border border-gold/30 rounded-sm p-8 max-w-md w-full space-y-5">
                        <div>
                            <div className="text-gold text-xs tracking-[0.4em] uppercase mb-1">Lascia una recensione</div>
                            <h2 className="font-serif text-3xl text-white">Com'è stata?</h2>
                        </div>

                        <div className="flex gap-2 justify-center" data-testid="review-stars">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button type="button" key={n} onClick={() => setReviewStars(n)}
                                    className="transition-transform hover:scale-110"
                                    data-testid={`star-${n}`}>
                                    <Star size={36} strokeWidth={1.5}
                                        className={n <= reviewStars ? "text-gold fill-gold" : "text-gray-600"} />
                                </button>
                            ))}
                        </div>

                        <div className="relative">
                            <MessageSquare className="absolute left-3 top-3 text-gray-500" size={16} strokeWidth={1.5} />
                            <textarea rows={4} value={reviewText} onChange={(e) => setReviewText(e.target.value)}
                                placeholder="Raccontaci la tua esperienza (opzionale)"
                                data-testid="review-text"
                                className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none text-sm resize-none" />
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setReviewing(null)} disabled={submitting}
                                className="text-xs text-gray-400 hover:text-white px-4 py-2.5 uppercase tracking-widest"
                                data-testid="review-cancel-btn">
                                Annulla
                            </button>
                            <button onClick={submitReview} disabled={submitting || reviewStars < 1}
                                className="bg-gold text-black font-semibold px-6 py-2.5 rounded-sm hover:bg-gold-light tracking-widest uppercase text-xs disabled:opacity-50"
                                data-testid="review-submit-btn">
                                {submitting ? "Invio..." : "Pubblica"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status, arrivalStatus }) {
    if (arrivalStatus === "arrived") {
        return <span className="text-[10px] uppercase tracking-widest border px-3 py-1.5 rounded-sm bg-emerald-500/10 text-emerald-400 border-emerald-500/30">Arrivato</span>;
    }
    if (arrivalStatus === "no_show") {
        return <span className="text-[10px] uppercase tracking-widest border px-3 py-1.5 rounded-sm bg-gray-500/10 text-gray-400 border-gray-500/30">No-show</span>;
    }
    const map = {
        pending: { label: "In attesa", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
        confirmed: { label: "Confermata", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
        cancelled: { label: "Annullata", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
    };
    const s = map[status] || map.pending;
    return <span className={`text-[10px] uppercase tracking-widest border px-3 py-1.5 rounded-sm ${s.cls}`}>{s.label}</span>;
}
